import { createSelector } from 'reselect'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage } from 'react-intl'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getSortedOrdersByDeparture } from 'selectors/orderSelectors'
import { isOrderCancelled } from 'lib/order/orderUtils'
import isUpcoming from 'lib/order/isUpcoming'
import { EXP_CATEGORY_AIRPORT_LOUNGE_ID } from 'constants/experience'
import LayoutContainer from 'components/Common/LayoutContainer'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'

const getOrdersWithExperienceItems = createSelector(
  getSortedOrdersByDeparture,
  (allOrders): Array<App.Order> =>
    allOrders.data.filter(order =>
      !isOrderCancelled(order) &&
      isUpcoming(order) &&
      order.experienceItems.some(item => item.status !== 'cancelled'),
    ),
)

const Section = styled.section`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
`

const Card = styled(ClickableLink)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(12)};
  padding-right: ${rem(24)};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(485)};
  }

  &:hover {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

const CardImageContainer = styled.div`
  width: ${rem(102)};
  min-height: ${rem(100)};
  align-self: stretch;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0;
  }
`

const CardTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

function LoungePassesYourPassesSection() {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const orders = useAppSelector(getOrdersWithExperienceItems)
  const experiences = useAppSelector(state => state.experience.experiences)
  const airports = useAppSelector(state => state.geo.airports)

  const allExperienceIds = useMemo(
    () => orders.flatMap(order => order.experienceItems.map(item => item.experienceId)),
    [orders],
  )

  useEffect(() => {
    if (allExperienceIds.length) {
      dispatch(fetchExperiencesById(...allExperienceIds))
    }
  }, [dispatch, allExperienceIds])

  const ordersWithLoungePasses = useMemo(() =>
    orders.filter(order =>
      order.experienceItems.some(item => {
        if (item.status === 'cancelled') return false
        const offer = experiences[item.experienceId]
        return offer?.categories?.some(cat => cat.id === EXP_CATEGORY_AIRPORT_LOUNGE_ID)
      }),
    ),
  [orders, experiences])

  if (!isLoggedIn || ordersWithLoungePasses.length === 0) return null

  const cards = ordersWithLoungePasses.map(order => {
    const loungeItems = order.experienceItems.filter(item => {
      if (item.status === 'cancelled') return false
      return experiences[item.experienceId]?.categories?.some(cat => cat.id === EXP_CATEGORY_AIRPORT_LOUNGE_ID)
    })
    const passCount = loungeItems.length
    const firstItem = loungeItems[0]
    const experienceOffer = firstItem ? experiences[firstItem.experienceId] : undefined
    const iataCode = firstItem?.experienceId.startsWith('cln:') ? firstItem.experienceId.slice(4) : undefined
    const airport = iataCode ? airports.find(a => a.code === iataCode) : undefined
    const title = airport?.name ?? firstItem?.title ?? ''
    const subtitle = experienceOffer?.name ?? ''
    const image = experienceOffer?.images?.[0]
    const tier = order.loyaltyDetails.tier

    return <Card key={order.id} to={`/account/my-escapes/order/${order.id}`}>
      <CardImageContainer>
        {image && <Image image={image} alt={title} fit="center" dpr={2} />}
      </CardImageContainer>
      <CardContent>
        <CardTextGroup>
          <BodyText variant="medium" weight="bold">{title}</BodyText>
          {subtitle && <BodyText variant="small">{subtitle}</BodyText>}
        </CardTextGroup>
        {tier && <LoyaltyTierLabel tier={tier} kind="primary">
          <FormattedMessage
            id="fRbTkg7YjOZTh9WPiLVB"
            defaultMessage="{count, plural, one {#x Pass} other {#x Passes}}"
            description="Number of lounge passes for a booking in the Your lounge passes section"
            values={{ count: passCount }}
          />
        </LoyaltyTierLabel>}
      </CardContent>
    </Card>
  })

  return <Section>
    <LayoutContainer variant="large">
      <Group direction="vertical" gap={20}>
        <Heading variant="heading4" align="center">
          <FormattedMessage
            id="FKlh-nsHvRZ5ZdIHU--I"
            defaultMessage="Your lounge passes"
            description="Heading for the lounge pass redemptions section on the lounge passes benefit page"
          />
        </Heading>
        <CSSBreakpoint max="mobile">
          <CardList>{cards}</CardList>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <CardCarousel>{cards}</CardCarousel>
        </CSSBreakpoint>
      </Group>
    </LayoutContainer>
  </Section>
}

export default LoungePassesYourPassesSection
